import { _ } from "../../../../../core/polyglot/polyglot.js";
import { circlePlusRegularIcon } from "../../../../../core/elements/icons/index.js";
import {
	cost,
	income,
} from "../../../../../models/forecast/ruleModels/AccountType.js";
import {
	EditState,
	ReadOnlyState,
} from "../RuleTemplatesWidget/RuleTemplatesState.js";
import { spacer, theme } from "../../../../../core/elements/index.js";
import { verticalDirection } from "../../../../../core/elements/stacker/stacker.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import addBalanceRuleModelFieldsToForm from "../ruleModelEditors/addBalanceRuleModelFieldsToForm.js";
import addPredictionRuleModelFieldsToForm from "../ruleModelEditors/addPredictionRuleModelFieldsToForm.js";
import AddRuleTemplateAction from "../RuleTemplatesWidget/actions/AddRuleTemplateAction.js";
import BulkRuleTemplateAction from "../RuleTemplatesWidget/actions/BulkRuleTemplateAction.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import FormBuilder from "../../../../../core/widgets/forms/FormBuilder.js";
import ManualEntryRuleModel from "../../../../../models/forecast/ruleModels/ManualEntryRuleModel.js";
import ManualEntryRuleTemplateCollectionWidget from "../ManualEntryRuleTemplateCollectionWidget/ManualEntryRuleTemplateCollectionWidget.js";
import NoRuleTemplateModel from "../RuleTemplatesWidget/RuleTemplateModel/NoRuleTemplateModel.js";
import NullRuleTemplateAction from "../RuleTemplatesWidget/actions/NullRuleTemplateAction.js";
import RemoveRuleTemplateAction from "../RuleTemplatesWidget/actions/RemoveRuleTemplateAction.js";
import RuleTemplate from "../../../../../models/forecast/template/RuleTemplate.js";
import RuleTemplateWidget from "../RuleTemplateWidget/RuleTemplateWidget.js";
import UpdateRuleTemplateAction from "../RuleTemplatesWidget/actions/UpdateRuleTemplateAction.js";

/**
 * Display collection of manual entry rules or edit form depending
 * the current state.
 *
 * @param {RuleTemplateModel[]} ruleTemplateModels - Model to pilot this widget
 * @param {Budget[]} budgets - Budgets for a report
 * @param {VatRate[]} vatRates - List of available VatRates
 * @param {int[]} paymentSpread - Default payment spread from ForecastConfiguration
 * @param {function} onBulkActionRequest - Function called with
 *   {bulkAction} as argument when the user is done editing rules
 * @param {AbstractRuleTemplateState} currentState - Used to coordinate the rendering
 *   with RuleTemplatesWidget.
 * @param {boolean} isCostAccountRow - Set if the selected account is cost or
 *   income account
 * @param {boolean} [isEditing] - When true, the content is in edition mode
 *   with some sort of forms to edit the rule templates. This only allows
 *   to change the value of a rule, not its type
 * @param {boolean} [readOnly] - If false the buttons 'Add', 'Edit', 'Delete',
 *   do not exist.
 */
export default class ManualEntryRuleTemplatesWidget extends Widget {
	constructor({
		row,
		column,
		ruleTemplateModels,
		budgets,
		vatRates,
		paymentSpread,
		onBulkActionRequest,
		currentState,
		isCostAccountRow,
		isEditing,
		readOnly,
	}) {
		super(...arguments);

		this._row = row;
		this._column = column;
		this._budgets = budgets;
		this._vatRates = vatRates;
		this._paymentSpread = paymentSpread;
		this._onBulkActionRequest = onBulkActionRequest;
		this._currentState = currentState;
		this._isCostAccountRow = isCostAccountRow;

		this.setRuleTemplateModels(ruleTemplateModels, {
			silently: true,
		});

		if (readOnly) {
			this._currentState = new ReadOnlyState();
		} else if (isEditing) {
			this._currentState = new EditState();
		}

		this._currentState.addWidget(this);

		this._manualEntryRuleTemplateWidget =
			this._makeManualEntryRuleTemplateWidget();
	}

	isEmpty() {
		return !this._ruleTemplateModels.length;
	}

	// We don't follow the guideline here when it comes to using an object parameter
	// as otherwise it makes calling this method really confusing, and the `silently` parameter
	// is only used locally.
	setRuleTemplateModels(ruleTemplateModels, { silently = false } = {}) {
		this._ruleTemplateModels = ruleTemplateModels;

		this._currentRuleTemplateModel = this._makeRuleTemplate({
			ruleModel: new ManualEntryRuleModel({
				accountType: this._isCostAccountRow ? cost : income,
				paymentSpread: this._paymentSpread,
			}),
		});

		this._manualEntryRuleTemplateWidget?.setRuleTemplateModel(
			this._currentRuleTemplateModel,
		);

		this._manualEntryRuleTemplateWidget?.setEditor(
			this._makeManualEntryRuleEditor(),
		);

		if (!silently) {
			this.update();
		}
	}

	changeState({ state }) {
		this._currentState = state;
		this._manualEntryRuleTemplateWidget.setState(state);
		this.update();
	}

	makeBulkEditAction(actions) {
		const bulkAction = new BulkRuleTemplateAction();
		bulkAction.appendActions(actions);

		return bulkAction;
	}

	makeNullRuleTemplateAction() {
		return new NullRuleTemplateAction();
	}

	makeAddRuleTemplateAction(ruleTemplate) {
		return new AddRuleTemplateAction({ ruleTemplate });
	}

	makeUpdateRuleTemplateAction(ruleTemplate) {
		return new UpdateRuleTemplateAction({ ruleTemplate });
	}

	makeRemoveRuleTemplateAction(ruleTemplate) {
		return new RemoveRuleTemplateAction({ ruleTemplate });
	}

	renderContentOn(html) {
		html.div(
			{
				class: "ManualEntryRuleTemplatesWidget",
				"data-test": "manual-entries",
			},
			html.div(
				{ class: "templates-container" },
				this._renderManualEntryRuleTitleOn.bind(this),
				this._renderManualEntryRuleTemplateOn.bind(this),
				this._renderButtonsOn.bind(this),
				this._renderManualEntryRuleTemplateCollectionWidgetOn.bind(
					this,
				),
			),
		);
	}

	renderEditingManualEntryRuleTitleOn(html) {
		spacer(
			{ class: "rule-title" },
			html.h2(
				{ "data-test": "manualEntry-edit-title" },
				ManualEntryRuleModel.getLabel(),
			),
		);
	}

	renderCreateButtonsOn(html) {
		html.div(
			spacer(
				{
					direction: verticalDirection,
					align: "left",
					spacing: "between",
					fullWidth: true,
				},
				html.h2(
					{ "data-test": "manual-entry-view-title" },
					this._currentState.getManualEntriesTitle(),
				),
				this._makeAddRuleButton(),
			),
		);
	}

	renderEditButtonsOn(html) {
		html.div(
			{ class: "edit-buttons" },
			html.span(
				_("{save} or {cancel}", {
					save: button(
						{
							dataTest: "save",
							primary: true,
							onClick: () => this._onSaveRequest(),
						},
						_("Save"),
					),
					cancel: button(
						{
							dataTest: "cancel",
							onClick: () => this._goToViewState(),
						},
						_("Cancel"),
					),
				}),
			),
		);
	}

	renderEditManualEntryRuleTemplateOn(html) {
		html.div(
			{ class: "drawer-section darker", "data-test": "manualEntry" },
			this._manualEntryRuleTemplateWidget,
		);
	}

	renderManualEntryRuleTemplateCollectionOn(html) {
		html.div(
			{ class: "drawer-section darker", "data-test": "manualEntry" },
			this._makeManualEntryRuleTemplateCollectionWidget(),
		);
	}

	_renderManualEntryRuleTitleOn(html) {
		html.render(this._currentState.manualEntryRuleTitleRenderer(this));
	}

	_renderButtonsOn(html) {
		html.render(this._currentState.manualEntryButtonsRenderer(this));
	}

	_renderManualEntryRuleTemplateOn(html) {
		html.render(this._currentState.manualEntryRuleTemplateRenderer(this));
	}

	_renderManualEntryRuleTemplateCollectionWidgetOn(html) {
		html.render(
			this._currentState.manualEntryRuleTemplateCollectionRenderer(this),
		);
	}

	_makeManualEntryRuleTemplateWidget() {
		return this._makeRuleTemplateWidget({
			editorDataTestName: "manualEntry-model-editor",
			predictionModelEditorClass: "prediction-model-editor",
			editor: this._makeManualEntryRuleEditor(),
		});
	}

	_makeManualEntryRuleTemplateCollectionWidget() {
		return new ManualEntryRuleTemplateCollectionWidget({
			ruleTemplateModels: this._ruleTemplateModels,
			onDelete: (ruleTemplate) => this._onDeleteRequest(ruleTemplate),
			onEdit: (ruleTemplate) => this._onEditRequest(ruleTemplate),
			readOnly: this._currentState.isReadOnly(),
		});
	}

	_makeRuleTemplateWidget({ editor, editorDataTestName }) {
		return new RuleTemplateWidget({
			budgets: this._budgets,
			ruleTemplateModel: this._currentRuleTemplateModel,
			editor,
			editorDataTestName,
			state: this._currentState,
			onCellSelectionRequest: () => {},
		});
	}

	_makeManualEntryRuleEditor() {
		const ruleModel = this._currentRuleTemplateModel.getRuleModel();

		const form = this._makeForm({
			ruleModel,
			class: "balance-rule-form",
		});

		this._currentRuleTemplateModel.setForm(form);

		addPredictionRuleModelFieldsToForm({
			form,
			ruleModel,
			budgets: this._budgets,
		});

		addBalanceRuleModelFieldsToForm({
			form,
			ruleModel,
			vatRates: this._vatRates,
		});

		return form;
	}

	_makeRuleTemplate({ ruleModel }) {
		const rowId = this._row.getStableId();
		const start = this._column.getStart();

		return new NoRuleTemplateModel({
			ruleTemplate: new RuleTemplate({
				rowId,
				start,
				ruleModel,
			}),
		});
	}

	_makeForm({ ruleModel, class: cssClass }) {
		return new FormBuilder({
			model: ruleModel,
			klass: [cssClass, "rule-form"],
			dataTestName: "save",
			showActions: false,
			showCancel: false,
			inputClass: "col-sm-12",
			labelClass: "col-sm-12",
		});
	}

	_makeAddRuleButton() {
		return theme(
			{ name: "manual-entry-editor-section" },
			button(
				{
					primary: true,
					dataTest: "add-manual-entry",
					icon: circlePlusRegularIcon,
					onClick: () =>
						this._currentState.goToEditManualEntryState(),
				},
				_("Add one off"),
			),
		);
	}

	_onSaveRequest() {
		const form = this._currentRuleTemplateModel.getForm();

		form.submit();

		if (!form.validate()) {
			return;
		}

		const bulkAction = this.makeBulkEditAction([
			this._currentRuleTemplateModel.makeSaveActionOn(this),
		]);

		bulkAction.perform((action) => {
			this._goToViewState();
			this._onBulkActionRequest({ bulkAction: action });
		});
	}

	_onDeleteRequest({ ruleTemplate }) {
		this._confirmDeletion({
			title: _("Delete one off rule?"),
			body: `${_(
				"Are you sure you want to delete the rule",
			)} ${ruleTemplate.getRuleTemplate().getRuleModel().getName()}?`,
			callback: () => this._execDelete({ ruleTemplate }),
		});
	}

	_execDelete({ ruleTemplate }) {
		const bulkAction = this.makeBulkEditAction([
			ruleTemplate.makeDeleteActionOn(this),
		]);

		bulkAction.perform((action) => {
			this._onBulkActionRequest({ bulkAction: action });
		});
	}

	_onEditRequest({ ruleTemplate }) {
		this._currentRuleTemplateModel = ruleTemplate;

		this._manualEntryRuleTemplateWidget?.setRuleTemplateModel(
			this._currentRuleTemplateModel,
		);

		this._manualEntryRuleTemplateWidget?.setEditor(
			this._makeManualEntryRuleEditor(),
		);

		this._currentState.goToEditManualEntryState();
	}

	_goToViewState() {
		this.setRuleTemplateModels(this._ruleTemplateModels, {
			silently: true,
		});

		this._currentState.goToViewState();
	}
}
